import type {
  ClaimRecord,
  DerivationTimeZone,
  ValueDerivation,
  ValueDerivationKind,
} from "../types";
import { readProperty } from "../types";

/**
 * The subset of a Column_Definition or an Export_Field that a derivation
 * reads. Both satisfy it structurally with no cast, which is what lets the
 * export builder reuse deriveDisplayValue and so guarantees an exported cell
 * equals the cell the table renders.
 */
export interface DerivableField {
  readonly source: string;
  readonly derivation?: ValueDerivation;
  readonly emptyPlaceholder?: string;
}

/** 9999-12-31T23:59:59Z, the largest instant this renders. */
const MAX_EPOCH_SECONDS = 253402300799;

/** Null means the derivation found no value to show. */
type DerivedValue = string | null;

type ValueDerivationRegistry = {
  readonly [K in ValueDerivationKind]: (
    derivation: Extract<ValueDerivation, { kind: K }>,
    source: string,
    claim: ClaimRecord
  ) => DerivedValue;
};

/**
 * The one accessor every derivation reads through, so a malformed row blanks
 * its own cells instead of throwing and taking the page with it.
 */
function readRawProperty(claim: ClaimRecord, source: string): unknown {
  if (claim === null || typeof claim !== "object") {
    return undefined;
  }
  return readProperty(claim, source);
}

/**
 * A claim property as a display string, or null when it holds no value.
 * Absence is all three of a missing key, a null value, and a zero-length
 * string. A value that is not a scalar has no display form, so it is treated
 * as absent rather than rendered as "[object Object]".
 */
function readDisplayScalar(claim: ClaimRecord, source: string): DerivedValue {
  const value = readRawProperty(claim, source);
  if (typeof value === "string") {
    return value === "" ? null : value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  return null;
}

function toEpochSeconds(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return null;
  }
  return value >= 0 && value <= MAX_EPOCH_SECONDS ? value : null;
}

const dateFormatters = new Map<DerivationTimeZone, Intl.DateTimeFormat>();

function dateFormatterFor(timeZone: DerivationTimeZone): Intl.DateTimeFormat {
  const cached = dateFormatters.get(timeZone);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat("en-US", {
    // Pinned so the parts are Gregorian year, month, and day in ASCII digits
    // whatever the environment's defaults are.
    calendar: "gregory",
    numberingSystem: "latn",
    // Omitting the option for "viewerLocal" lets Intl resolve the runtime zone.
    ...(timeZone === "utc" ? { timeZone: "UTC" } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  dateFormatters.set(timeZone, formatter);
  return formatter;
}

function partValue(
  parts: readonly Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string {
  return parts.find((part) => part.type === type)?.value ?? "";
}

/**
 * Epoch seconds as a zero-padded `YYYY-MM-DD` resolved in the given zone, or a
 * zero-length string for a value that is not a whole number of seconds in
 * 0..253402300799 inclusive.
 *
 * Intl resolves the UTC offset of the instant being formatted. The offset
 * arithmetic this replaces used `new Date().getTimezoneOffset()`, the offset of
 * now rather than of the instant, so a claim timestamped near local midnight on
 * the far side of a daylight-saving transition rendered a day off. formatToParts
 * is used rather than a locale that happens to format ISO-like, so the output
 * does not depend on the ICU version.
 */
export function formatEpochSecondsAsDate(
  seconds: number,
  timeZone: DerivationTimeZone = "utc"
): string {
  const epochSeconds = toEpochSeconds(seconds);
  if (epochSeconds === null) {
    return "";
  }
  const parts = dateFormatterFor(timeZone).formatToParts(new Date(epochSeconds * 1000));
  const year = partValue(parts, "year").padStart(4, "0");
  const month = partValue(parts, "month").padStart(2, "0");
  const day = partValue(parts, "day").padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * The implemented derivations, keyed by the name a config declares. The
 * validator checks a declared name against these keys, so an unknown name is
 * caught before render rather than blanking a column.
 */
export const VALUE_DERIVATIONS: ValueDerivationRegistry = {
  epochSecondsToDate: (derivation, source, claim) => {
    const seconds = toEpochSeconds(readRawProperty(claim, source));
    if (seconds === null) {
      return null;
    }
    return formatEpochSecondsAsDate(seconds, derivation.timeZone ?? "utc");
  },

  optionLabel: (derivation, source, claim) => {
    const value = readDisplayScalar(claim, source);
    if (value === null) {
      return null;
    }
    const matched = derivation.options.find((option) => option.value === value);
    return matched ? matched.label : (derivation.unmatchedText ?? null);
  },

  /**
   * Only the parts that hold a value are joined, so the separator cannot lead,
   * trail, or repeat. An absent required part yields no value at all, which is
   * a deliberate correction: the body-part concatenation this replaces had no
   * null guard and rendered the literal string "undefined".
   */
  concatenate: (derivation, source, claim) => {
    const present: string[] = [];
    for (const part of derivation.parts) {
      const value = readDisplayScalar(claim, part.source);
      if (value === null) {
        if (part.required) {
          return null;
        }
        continue;
      }
      present.push(value);
    }
    return present.join(derivation.separator);
  },
};

function applyDerivation(
  derivation: ValueDerivation,
  source: string,
  claim: ClaimRecord
): DerivedValue {
  switch (derivation.kind) {
    case "epochSecondsToDate":
      return VALUE_DERIVATIONS.epochSecondsToDate(derivation, source, claim);
    case "optionLabel":
      return VALUE_DERIVATIONS.optionLabel(derivation, source, claim);
    case "concatenate":
      return VALUE_DERIVATIONS.concatenate(derivation, source, claim);
    default: {
      // A kind added to `ValueDerivation` without a case above no longer
      // satisfies this `never` binding and stops compiling here, instead of
      // silently blanking every cell that declares it. Returning null keeps the
      // function total for a document the validator has not accepted, where
      // `kind` can be a string the union excludes.
      //
      // Indexing `VALUE_DERIVATIONS` by `derivation.kind` would collapse the
      // switch, but distributing the function type over the union needs a cast
      // that also stops checking each derivation against its own payload type.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustive: never = derivation;
      return null;
    }
  }
}

/**
 * The display string for one column or export field of one claim. Never throws:
 * a value the derivation cannot use yields the declared placeholder, or a
 * zero-length string when none is declared, and the row's other cells render
 * normally.
 */
export function deriveDisplayValue(definition: DerivableField, claim: ClaimRecord): string {
  const derived = definition.derivation
    ? applyDerivation(definition.derivation, definition.source, claim)
    : readDisplayScalar(claim, definition.source);
  if (derived === null || derived === "") {
    return definition.emptyPlaceholder ?? "";
  }
  return derived;
}
